import { Alert, Badge, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

const DEFAULT_TOOLTIP =
  'This value is scrambled (obfuscated) — not real market data. It is shown because you are not authorized to view real values. Do not use it for decisions, calculations, comparisons, or exports.';

/**
 * Inline indicator that the accompanying market-value numbers were obfuscated by
 * the data scrambler (`isScrambled === true`). Renders nothing when `scrambled`
 * is falsy.
 *
 * Styled consistently with the app's red "danger" cues (`color='red'
 * variant='light'`). For section-level placement prefer {@link ScrambledDataBanner}.
 */
export function ScrambledDataBadge({
  scrambled,
  size = 'sm',
  label = 'Scrambled — not a real value',
  tooltip = DEFAULT_TOOLTIP,
}: {
  /** Whether the associated data is scrambled. When false/undefined, nothing renders. */
  scrambled?: boolean;
  size?: string;
  label?: string;
  tooltip?: string;
}) {
  if (!scrambled) return null;

  return (
    <Tooltip label={tooltip} withArrow multiline w={300}>
      <Badge
        color='red'
        variant='light'
        size={size}
        leftSection={<IconAlertTriangle size={12} />}
        style={{ cursor: 'help', textTransform: 'none' }}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}

/**
 * Full-width banner announcing that the market-value data in this view is sample
 * (scrambled) data. Renders nothing when `scrambled` is falsy. Use at the top of
 * any section/card that surfaces scrambled values.
 *
 * Styled consistently with the app's other red danger banners
 * (`color='red' variant='light'`, `IconAlertTriangle`).
 */
export function ScrambledDataBanner({
  scrambled,
  title = 'Market value data is scrambled',
  message = 'You are not authorized to view real market values, so all market value figures on this screen are scrambled sample data. Do not use the market value figures for decisions, calculations, comparisons, or exports.',
}: {
  scrambled?: boolean;
  title?: string;
  message?: string;
}) {
  if (!scrambled) return null;

  return (
    <Alert
      color='red'
      variant='light'
      icon={<IconAlertTriangle size={16} />}
      title={title}
    >
      {message}
    </Alert>
  );
}

